import React from 'react';
import { Helmet } from 'react-helmet-async';
import styled from 'styled-components';
import { FaHome, FaBuilding, FaIndustry, FaHospital, FaServer, FaLeaf } from 'react-icons/fa';
import { IconWrapper } from '../components/icons/IconWrapper';

// Page Container
const PageContainer = styled.div`
  background-color: ${({ theme }) => theme.colors.darkest};
  color: ${({ theme }) => theme.colors.light};
  min-height: 100vh;
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 4rem 1rem 6rem;
`;

const HeaderSection = styled.div`
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.dark}, ${({ theme }) => theme.colors.darker});
  padding: 6rem 1rem 4rem;
  border-bottom: 2px solid ${({ theme }) => theme.colors.primary};
`;

// Page Header
const PageHeader = styled.div`
  text-align: center;
  max-width: 1000px;
  margin: 0 auto;
`;

const PageTitle = styled.h1`
  font-size: 3.5rem;
  margin-bottom: 1.5rem;
  color: ${({ theme }) => theme.colors.light};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 2.5rem;
  }
`;

const PageDescription = styled.p`
  font-size: 1.25rem;
  max-width: 800px;
  margin: 0 auto;
  color: ${({ theme }) => theme.colors.lightGray};
  line-height: 1.6;
`;

// Industries Grid
const IndustriesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 2rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

// Industry Card
const IndustryCard = styled.div`
  background-color: ${({ theme }) => theme.colors.darker};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.shadows.medium};
  transition: ${({ theme }) => theme.transitions.medium};
  height: 100%;
  border: 1px solid rgba(255, 255, 255, 0.05);
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: ${({ theme }) => theme.shadows.large};
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const IndustryHeader = styled.div`
  background: linear-gradient(to right, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary});
  padding: 1.5rem;
  display: flex;
  align-items: center;
`;

const IconContainer = styled.div`
  width: 50px;
  height: 50px;
  background-color: ${({ theme }) => theme.colors.dark};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 1.5rem;
  box-shadow: ${({ theme }) => theme.shadows.small};
`;

const IndustryTitle = styled.h2`
  color: ${({ theme }) => theme.colors.light};
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
`;

const IndustryContent = styled.div`
  padding: 1.5rem;
  background-color: ${({ theme }) => theme.colors.darker};
`;

const IndustryDescription = styled.p`
  color: ${({ theme }) => theme.colors.lightGray};
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;

const IndustryFeatures = styled.ul`
  padding-left: 1.5rem;
  margin-bottom: 1.5rem;
`;

const IndustryFeature = styled.li`
  color: ${({ theme }) => theme.colors.lightGray};
  margin-bottom: 0.5rem;
  line-height: 1.5;
  
  &::marker {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

// Industry Page Component
const IndustriesPage: React.FC = () => {
  const industries = [
    {
      id: 'residential',
      name: 'Residential',
      icon: FaHome,
      description: 'Safe and reliable wiring solutions for homes and residential buildings.',
      features: [
        'Residential building wire for new construction and renovations',
        'Service entrance cables for reliable power delivery',
        'Structured cabling for home networking and automation',
        'Fire-rated cables for safety systems',
        'Low voltage cables for smart home technology'
      ]
    },
    {
      id: 'commercial',
      name: 'Commercial',
      icon: FaBuilding,
      description: 'High-performance wiring for offices, retail spaces, and commercial properties.',
      features: [
        'Commercial-grade power distribution cables',
        'Fire alarm and security system wiring',
        'Building management system cables',
        'Structured cabling for data networks',
        'Energy-efficient lighting control systems'
      ]
    },
    {
      id: 'industrial',
      name: 'Industrial',
      icon: FaIndustry,
      description: 'Durable cables designed for demanding industrial environments and applications.',
      features: [
        'Durable cables for harsh factory environments',
        'Armored cables for maximum protection',
        'Motor and VFD cables for machinery',
        'Control and instrumentation cables',
        'High-temperature cables for extreme conditions'
      ]
    },
    {
      id: 'healthcare',
      name: 'Healthcare',
      icon: FaHospital,
      description: 'Specialized wiring solutions for hospitals and healthcare facilities.',
      features: [
        'Antimicrobial cable jackets for healthcare environments',
        'Low-smoke zero-halogen cables for patient safety',
        'Medical-grade power cords and cables',
        'Critical system backup power cabling',
        'Nurse call and patient monitoring system cables'
      ]
    },
    {
      id: 'data-centers',
      name: 'Data Centers',
      icon: FaServer,
      description: 'High-capacity power and network cabling for data center infrastructure.',
      features: [
        'High-density power distribution cables',
        'Category 6A/7/8 data cables for high-speed networks',
        'Fiber optic backbone cabling',
        'Precision cooling system connections',
        'Redundant backup power cabling'
      ]
    },
    {
      id: 'green-energy',
      name: 'Green Energy',
      icon: FaLeaf,
      description: 'Specialized cables for solar, wind, and other renewable energy applications.',
      features: [
        'Solar PV array cabling and connectors',
        'Wind turbine control and power cables',
        'Battery storage system connections',
        'Weather-resistant outdoor cables',
        'High-voltage DC transmission cables'
      ]
    }
  ];

  const pageTitle = 'Industries Served | ElectroCable Wire & Cable Solutions';
  const pageDescription = 'ElectroCable provides specialized wire and cable solutions for residential, commercial, industrial, healthcare, data centers, and green energy sectors. Discover our expertise in tailoring products to your industry needs.';
  const siteUrl = 'https://www.yourwebsite.com'; // Replace with your actual domain
  const industriesPageUrl = `${siteUrl}/industries`;
  const ogImageUrl = `${siteUrl}/images/industries-og-image.jpg`; // Replace with a relevant OG image

  return (
    <PageContainer>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={industriesPageUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={ogImageUrl} />
        <meta property="og:site_name" content="ElectroCable" />
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={industriesPageUrl} />
        <meta property="twitter:title" content={pageTitle} />
        <meta property="twitter:description" content={pageDescription} />
        <meta property="twitter:image" content={ogImageUrl} />

        {/* JSON-LD Structured Data for ItemList (Industries) */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            "name": "Industries Served by ElectroCable",
            "description": "A list of primary industries ElectroCable provides wire and cable solutions for.",
            "itemListElement": industries.map((industry, index) => ({
              "@type": "ListItem",
              "position": index + 1,
              "item": {
                "@type": "Service", // Or use "Product" if each industry links to specific product categories
                "name": industry.name,
                "description": industry.description,
                // "url": `${industriesPageUrl}#${industry.id}` // Optional: Link to an anchor on the page if available
              }
            }))
          })}
        </script>
      </Helmet>

      <HeaderSection>
        <PageHeader>
          <PageTitle>Industries We Serve</PageTitle>
          <PageDescription>
            ElectroCable delivers specialized wire and cable solutions tailored to the unique
            requirements of a wide range of industries, ensuring optimal performance
            and reliability for every application.
          </PageDescription>
        </PageHeader>
      </HeaderSection>

      <ContentWrapper>
        <IndustriesGrid>
          {industries.map((industry) => (
            <IndustryCard key={industry.id}>
              <IndustryHeader>
                <IconContainer>
                  <IconWrapper icon={industry.icon} />
                </IconContainer>
                <IndustryTitle>{industry.name}</IndustryTitle>
              </IndustryHeader>
              <IndustryContent>
                <IndustryDescription>{industry.description}</IndustryDescription>
                <IndustryFeatures>
                  {industry.features.map((feature, index) => (
                    <IndustryFeature key={index}>{feature}</IndustryFeature>
                  ))}
                </IndustryFeatures>
              </IndustryContent>
            </IndustryCard>
          ))}
        </IndustriesGrid>
      </ContentWrapper>
    </PageContainer>
  );
};

export default IndustriesPage; 